import type { EntryCollection, EntrySkeletonType } from 'contentful'

interface Sys {
  id: string
}

export type CntflLocales = 'en-AU' | 'en-NZ' | 'en-CA' | 'fr-CA'

export interface ContentfulParams {
  content_type?:
    | 'accordionPanel'
    | 'blogArticle'
    | 'cartPreferences'
    | 'connectedContentPillar'
    | 'contentPage'
    | 'pageContentSlots'
    | 'pdpPreferences'
    | 'productListingPage'
    | 'siteHeader'
    | 'siteFooter'
    | 'sitePreferences'
    | 'slug'
  'sys.id'?: string
  'sys.contentType.sys.id[in]'?: string[]
  'fields.slug'?: string
  'fields.locale'?: CntflLocales
  'fields.category'?: string
  'fields.category[in]'?: string[]
  'fields.categoryId'?: string
  'sys.publishedVersion[exists]'?: boolean
  include?: number // 10 to include nested level content in response
  locale?: CntflLocales
  limit?: number
  order?: string
  preview?: boolean
}

export interface ContentfulPreviewParams extends ContentfulParams {
  previewId?: string
  previewEnv?: string
  links_to_entry?: string
  previewByDate?: string
  'sys.status'?: string
}

export interface Entry {
  sys: Sys
  fields: { [key: string]: any }
}

export interface Result {
  id: string
  fields: { [key: string]: any }
}

export const mapItems = (
  item: Entry | Entry[] | undefined,
  includes: Entry[] | undefined,
  entries: Result[],
  name?: string,
): void => {
  if (!item) return
  const links = Array.isArray(item) ? item : [item]
  links.forEach((link) => {
    const id = link.sys.id
    // Links arrive resolved; slim responses omit `includes`.
    let foundItem = link.fields ? link : includes?.find((entry) => entry.sys.id === id)
    if (foundItem) {
      if (name) {
        foundItem = foundItem.fields[name]
        mapItems(foundItem, includes, entries)
      } else {
        entries.push({ id, fields: foundItem.fields })
      }
    }
  })
}

// Map Contentful response to max 2 levels deep
export const mapEntries = (items: Entry[], includes: Entry[] | undefined, name: string, subName?: string): Result[] => {
  const entries: Result[] = []
  const item = items.find(({ fields }) => fields[name])
  if (item) {
    const fieldItem = item.fields[name]
    mapItems(fieldItem, includes, entries, subName)
  }
  return entries
}

const isNotDraft = (entity: any) => {
  /*
   Check if entry is not in Draft state. 
   
  @TODO explore later: https://www.contentful.com/developers/docs/tutorials/general/determine-entry-asset-state/

    function isDraft(entity) {
    return !entity.sys.publishedVersion;
    }
    function isChanged(entity) {
    return !!entity.sys.publishedVersion && entity.sys.version >= entity.sys.publishedVersion + 2;
    }
    function isPublished(entity) {
    return !!entity.sys.publishedVersion && entity.sys.version === entity.sys.publishedVersion + 1;
    }
    function isArchived(entity) {
    return !!entity.sys.archivedVersion;
    }
  */
  return !!entity?.sys?.publishedVersion
}

export const filterPublished = (entities: any[] = []) => entities.filter(isNotDraft)

export const getCacheTag = (entryId: string) => `Tag:${entryId}`

export const replaceFieldsInText = (text: string, values: { [key: string]: any }, locale: CntflLocales): string => {
  const findFields = (text || '').match(/{{(.*?)}}/g)?.map((field) => field.replace('{{', '').replace('}}', ''))
  if (findFields) {
    return findFields.reduce((acc, field) => {
      const value =
        typeof values[field] === 'number'
          ? values[field]?.toLocaleString(locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : (values[field]?.toString() ?? null)
      return value ? acc.replace(`{{${field}}}`, value) : acc
    }, text)
  }
  return text
}

export function toContentfulSearchParams(params: ContentfulParams & ContentfulPreviewParams) {
  const sp = new URLSearchParams()

  // limitation of nest is for keys such as 'fields.category' and 'fields.category[in]' nest will incorrectly evaluate the field validation as it conflicts
  // so for any instance of [foo] in keyname we replace with _foo_ to allow proper validation eg fields.category[in] becomes fields.category_in_ and we will reverse it in nest
  Object.entries(params).forEach(([key, value]) => {
    const newKey = key.replace(/\[(.*?)\]$/, '_$1_')
    if (value === undefined || value === null) return

    if (Array.isArray(value)) {
      value.forEach((v) => {
        sp.append(newKey, v)
      })
      return
    }

    if (typeof value === 'boolean' || typeof value === 'number') {
      sp.set(newKey, String(value))
      return
    }

    sp.set(newKey, value)
  })

  return sp
}

/**
 * Retrieves a nested value from an object using a key path array
 * @param obj - The object to traverse
 * @param keyPath - Array of keys to follow. Use 'key:index' syntax to access array elements
 * @returns The value at the nested path, or undefined if path doesn't exist
 * @example
 * getNestedValue(entries, ['items:0', 'sys', 'revision']) --> entries.items[0].sys.revision
 * getNestedValue(entries.items[0], ['sys','revision']) --> entries.items[0].sys.revision
 *
 */
export const getNestedValue = <T>(obj: T, keyPath: string[]) => {
  return keyPath.reduce((current: any, key: string) => {
    if (current == null) return undefined

    if (key.includes(':')) {
      const [prop, index] = key.split(':')
      return current[prop]?.[parseInt(index || '0', 10)]
    }

    return current[key]
  }, obj)
}

/**
 * Search for and retrieve a nested item by sys.id, filtering out other items
 * @param obj - The object to search in
 * @param keyPath - Array of keys to follow. Use 'key:sys.id' to search arrays by sys.id
 * @param targetId - The sys.id value to search for
 * @returns The matching item or array containing only the matching item, or undefined if not found
 * @example
 * getNestedItem(entries, ['items:sys.id'], '4Yj6dr1nMZ3blbxODxMu6N') --> finds item with matching sys.id
 * getNestedItem(entries, ['items:0', 'fields', 'blogArticle:sys.id'], 'abc123') --> finds article in blogArticle array
 */
export const getNestedItem = <T>(obj: T, keyPath: string[], targetId: string) => {
  return keyPath.reduce((current: any, key: string, index: number) => {
    if (current == null) return undefined

    // Handle search by dynamic property syntax (key:property.path)
    if (key.includes(':') && !key.match(/^\d+$/)) {
      const [prop, searchPath] = key.split(':')
      const array = current[prop]

      if (!Array.isArray(array)) return undefined

      // Find the item with matching nested property
      const foundItem = array.find((item: any) => {
        const value = getNestedValue(item, searchPath.split('.'))
        return value === targetId
      })

      return foundItem
    }

    // Handle array index syntax (key:0)
    if (key.includes(':') && !key.includes(':sys.id')) {
      const [prop, arrayIndex] = key.split(':')
      return current[prop]?.[parseInt(arrayIndex || '0', 10)]
    }

    return current[key]
  }, obj)
}

// Helper to filter embedded entries in rich text content based on personalization tags
export const filterArrayItemByTag = (item, tag: string) => {
  if (!hasPersonalizationTag(item, tag)) return null

  if (item?.fields?.content?.nodeType && Array.isArray(item.fields.content.content)) {
    return {
      ...item,
      fields: {
        ...item.fields,
        content: filterRichTextByPersonalizationTag(item.fields.content, tag),
      },
    }
  }

  return item
}

// Helper to process array items with potential rich text content
export const filterRichTextByPersonalizationTag = (content, tag: string) => {
  if (!content?.nodeType || !Array.isArray(content.content)) return content

  return {
    ...content,
    content: content.content.map((contentItem) => {
      if (contentItem?.content && Array.isArray(contentItem.content)) {
        return {
          ...contentItem,
          content: contentItem.content.filter((contentNode) => {
            return hasPersonalizationTag(contentNode?.data?.target, tag)
          }),
        }
      }
      return contentItem
    }),
  }
}

export const processNestedFieldsByTag = (fields: Record<string, unknown>, tag: string): Record<string, unknown> => {
  if (!fields || typeof fields !== 'object') return fields

  const processedFields = { ...fields }

  Object.entries(processedFields).forEach(([fieldName, fieldValue]) => {
    // Handle arrays
    if (Array.isArray(fieldValue)) {
      processedFields[fieldName] = fieldValue
        .filter((item: unknown) => {
          if (!item || typeof item !== 'object' || !('fields' in item)) return true
          const itemFields = (item as Entry).fields
          const tags = (itemFields?.personalizationTags as string[]) || []
          return tags.length === 0 || tags.includes(tag)
        })
        .map((item: unknown) => {
          if (item && typeof item === 'object' && 'fields' in item) {
            const entry = item as Entry
            return {
              ...entry,
              fields: processNestedFieldsByTag(entry.fields, tag),
            }
          }
          return item
        })
    }
    // Handle rich text content
    else if (fieldValue && typeof fieldValue === 'object' && 'nodeType' in fieldValue && 'content' in fieldValue) {
      processedFields[fieldName] = filterRichTextByPersonalizationTag(fieldValue, tag)
    }
    // Handle nested objects with fields property
    else if (fieldValue && typeof fieldValue === 'object' && 'fields' in fieldValue) {
      const entry = fieldValue as Entry
      const tags = (entry.fields?.personalizationTags as string[]) || []

      // Remove if has tags but doesn't match
      if (tags.length > 0 && !tags.includes(tag)) {
        delete processedFields[fieldName]
      } else {
        // Recursively process nested fields
        processedFields[fieldName] = {
          ...entry,
          fields: processNestedFieldsByTag(entry.fields, tag),
        }
      }
    }
  })

  return processedFields
}

export const hasPersonalizationTag = (item, tag: string): boolean => {
  const tags = item?.fields?.personalizationTags || []
  return !Array.isArray(tags) || tags.length === 0 || tags.includes(tag)
}

export const hasFields = (item): item is { fields } => item && typeof item === 'object' && 'fields' in item

export const extractPanelId = (value: any): string | null => (value as any)?.sys?.id || null

export const isRichTextContent = (value): boolean => {
  return value && typeof value === 'object' && Array.isArray(value?.content)
}

/*
 * Market visibility (MHJ-261105) — preview-only twin of the middleware's marketFilter.ts, which
 * does the real production filtering. Separate deployables, so changes must be mirrored in both.
 */

// Containers collapse when filtering empties every child, so no market renders a bare heading.
const MARKET_CONTAINER_FIELDS: Record<string, string> = {
  cardPanel: 'cards',
  sectionPanel: 'panels',
  columnPanel: 'panels',
}

const MARKET_EMBEDDED_ENTRY_NODE_TYPES = new Set(['embedded-entry-block', 'embedded-entry-inline'])

interface MarketRichTextNode {
  nodeType: string
  data?: { target?: unknown; [key: string]: unknown }
  content?: unknown[]
}

const isMarketRichTextNode = (value: unknown): value is MarketRichTextNode =>
  !!value && typeof value === 'object' && typeof (value as MarketRichTextNode).nodeType === 'string'

export const processNestedFieldsByMarket = (
  fields: Record<string, unknown>,
  market: string,
  visiting: Set<object>,
): Record<string, unknown> => {
  if (!fields || typeof fields !== 'object') return fields

  const processedFields = { ...fields }

  Object.entries(processedFields).forEach(([fieldName, fieldValue]) => {
    if (Array.isArray(fieldValue)) {
      processedFields[fieldName] = fieldValue
        .map((item: unknown) => filterEntryByMarket(item, market, visiting))
        .filter((item) => item !== null && item !== undefined)
      return
    }

    if (fieldValue && typeof fieldValue === 'object' && 'fields' in fieldValue) {
      const filtered = filterEntryByMarket(fieldValue, market, visiting)
      if (filtered === null) {
        delete processedFields[fieldName]
      } else {
        processedFields[fieldName] = filtered
      }
      return
    }

    // Rich Text embeds entries as nodes, not as a `fields`-shaped value or array, so it bypasses
    // both checks above and needs its own walk.
    if (isMarketRichTextNode(fieldValue)) {
      processedFields[fieldName] = filterRichTextNodeByMarket(fieldValue, market, visiting)
    }
  })

  return processedFields
}

// Excluded embeds are dropped from the parent's `content` array; an orphaned node with no
// `data.target` would blank-render or throw in the rich-text renderer.
const filterRichTextNodeByMarket = (node: unknown, market: string, visiting: Set<object>): unknown => {
  if (!isMarketRichTextNode(node)) return node

  if (MARKET_EMBEDDED_ENTRY_NODE_TYPES.has(node.nodeType)) {
    const target = node.data?.target
    if (!hasFields(target)) return node

    const filteredTarget = filterEntryByMarket(target, market, visiting)
    return filteredTarget === null ? null : { ...node, data: { ...node.data, target: filteredTarget } }
  }

  if (!Array.isArray(node.content)) return node

  return {
    ...node,
    content: node.content
      .map((child) => filterRichTextNodeByMarket(child, market, visiting))
      .filter((child) => child !== null),
  }
}

// `visiting` is an in-progress stack, not a seen-set: the resolved graph (`include: 10`) can
// cycle, so revisiting returns a sys-only stub while diamond/shared references still resolve.
export const filterEntryByMarket = (entry: unknown, market: string, visiting: Set<object> = new Set()): unknown => {
  if (!hasFields(entry)) return entry
  if (visiting.has(entry)) return { sys: (entry as any).sys }

  const fields = entry.fields
  if (!marketControl(fields?.markets as Markets, market)) return null

  visiting.add(entry)
  const filteredFields = processNestedFieldsByMarket(fields, market, visiting)
  visiting.delete(entry)

  const containerField = MARKET_CONTAINER_FIELDS[(entry as any).sys?.contentType?.sys?.id ?? '']
  if (containerField) {
    const before = fields[containerField]
    const after = filteredFields[containerField]
    const emptiedByMarketFilter =
      Array.isArray(before) && before.length > 0 && !(Array.isArray(after) && after.length > 0)
    if (emptiedByMarketFilter) return null
  }

  return { ...entry, fields: filteredFields }
}

// Top-level items are pages and are never removed or collapsed, even if one carries `markets` —
// a page dropped in preview but not production would render blank for no visible reason.
export const filterEntriesByMarket = (
  entries: EntryCollection<EntrySkeletonType, undefined, string>,
  market: string,
): EntryCollection<EntrySkeletonType, undefined, string> => {
  if (!market) return entries

  const visiting = new Set<object>()

  const filteredItems = entries.items.map((item) =>
    hasFields(item) ? { ...item, fields: processNestedFieldsByMarket(item.fields, market, visiting) } : item,
  ) as typeof entries.items

  if (!Array.isArray(entries.includes?.Entry)) {
    return { ...entries, items: filteredItems }
  }

  // Unlike items, includes.Entry entries are link targets, not pages — an excluded one is
  // removed outright rather than merely stripped of fields (mapEntries just won't find it).
  const filteredEntries = entries.includes.Entry.map((entry) => filterEntryByMarket(entry, market, visiting)).filter(
    (entry) => entry !== null && entry !== undefined,
  )

  return {
    ...entries,
    items: filteredItems,
    includes: { ...entries.includes, Entry: filteredEntries },
  } as typeof entries
}

export const shouldKeepPanel = (
  itemId: string | null,
  panelDisplayInfo: Record<string, { show: boolean | string }>,
): boolean => !itemId || panelDisplayInfo[itemId]?.show !== false

export const getFirstPageFields = (
  entries: EntryCollection<EntrySkeletonType, undefined, string>,
): Record<string, any> => {
  const items = entries?.items
  if (!items?.length) return {}

  const fields = items[0]?.fields
  return typeof fields === 'object' && fields ? fields : {}
}

export const filterArrayByDisplayInfo = (
  items: any[],
  panelDisplayInfo: Record<string, { show: boolean | string }>,
) => {
  if (!Array.isArray(items)) return items
  return items.filter((item) => shouldKeepPanel((item as any)?.sys?.id, panelDisplayInfo))
}

export const filterContentByDisplayInfo = (
  content: any[],
  panelDisplayInfo: Record<string, { show: boolean | string }>,
) => {
  if (!Array.isArray(content)) return content
  return content
    .filter((node) => {
      const targetId = node.data?.target?.sys?.id
      return shouldKeepPanel(targetId, panelDisplayInfo)
    })
    .filter((node) => !Array.isArray(node.content) || node.content.length > 0)
}

export const extractPanelIds = (items: any[]): string[] => {
  if (!Array.isArray(items)) {
    const id = extractPanelId(items)
    return id ? [id] : []
  }
  return items.map(extractPanelId).filter((id): id is string => id !== null)
}

export const extractPanelsFromRichTextContent = (content: any): LocalePageEntry[] => {
  const panels: LocalePageEntry[] = []

  if (!Array.isArray(content?.content)) return panels

  const extractFromContentArray = (contentArray: any[]) => {
    contentArray.forEach((item: any) => {
      // Check if this node itself is an embedded-entry-inline
      if (item.nodeType && item.data?.target) {
        const target = item.data?.target
        if (target?.fields && target?.sys) {
          panels.push(target as LocalePageEntry)
        }
      }

      // Recursively check nested content arrays example within paragraph nodes
      if (Array.isArray(item.content)) {
        extractFromContentArray(item.content)
      }
    })
  }

  extractFromContentArray(content.content)

  return panels
}

export const extractPanelsFromArray = (items: any[]): LocalePageEntry[] => {
  if (!Array.isArray(items)) return []

  return items.map((item) => extractPanelFromValue(item)).filter((panel): panel is LocalePageEntry => panel !== null)
}

export const extractPanelsFromObject = (obj: any): LocalePageEntry[] => {
  const panels: LocalePageEntry[] = []

  if (!obj || typeof obj !== 'object') return panels

  Object.values(obj).forEach((item: any) => {
    const panel = extractPanelFromValue(item)
    if (panel) panels.push(panel)
  })

  return panels
}

export const extractPanelFromValue = (value: any): LocalePageEntry | null => {
  if (value && typeof value === 'object' && 'sys' in value && value.sys?.id) {
    return value as LocalePageEntry
  }
  return null
}

const buildPanelIds = (panels: PanelInfo[], previewId?: string): string[] =>
  Array.from(new Set([...panels.map((panel) => panel.id), ...(previewId ? [previewId] : [])]))

export type RemovePanelsForDisplayInfoOptions = {
  panels?: PanelInfo[]
  scheduledActions?: Record<string, any>
}

export const shouldHidePreviewPayload = (
  previewConfig: PreviewConfig,
  panelDisplayInfo: Record<string, { show: boolean | string }>,
  options?: RemovePanelsForDisplayInfoOptions,
): boolean => {
  const hasDraftUnscheduledParent =
    Boolean(previewConfig.previewByDate) &&
    (options?.panels || [])
      .filter((panel) => panel.isParentPage)
      .some(
        (parent) =>
          parent.status.isDraft && ((options?.scheduledActions?.[parent.id]?.schedule || []) as any[]).length === 0,
      )

  const shouldHidePreviewPage =
    Boolean(previewConfig.previewId) && panelDisplayInfo?.[previewConfig.previewId]?.show === false

  return hasDraftUnscheduledParent || shouldHidePreviewPage
}

export const filterByPreviewDateOrchestration = async (
  entries: EntryCollection<EntrySkeletonType, undefined, string>,
  previewConfig: PreviewConfig,
  getAllPanels: (entries: EntryCollection<EntrySkeletonType, undefined, string>, config: PreviewConfig) => PanelInfo[],
  removePanelsForDisplayInfo: (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    displayInfo: Record<string, any>,
    previewConfig: PreviewConfig,
    options?: RemovePanelsForDisplayInfoOptions,
  ) => EntryCollection<EntrySkeletonType, undefined, string>,
) => {
  const panels = getAllPanels(entries, previewConfig)
  const panelIds = buildPanelIds(panels, previewConfig.previewId)
  const { getAllScheduledActions, getEntryDisplayInfo } = useScheduledActions()

  const scheduledActions = await getAllScheduledActions(panelIds, previewConfig.previewEnv, panels)
  const panelDisplayInfo = getEntryDisplayInfo(panelIds, previewConfig.previewByDate, scheduledActions as any, panels)

  return removePanelsForDisplayInfo(entries, panelDisplayInfo, previewConfig, {
    panels,
    scheduledActions,
  })
}
